import { Truck, Clock, MapPin, AlertOctagon } from "lucide-react";

/**
 * Shipping Policy — verbatim underwriter-approved language. Keeps the required
 * core points: same/next-day cutoff, US-only, no PO Boxes, tracking on
 * dispatch, and carrier-liability disclaimer.
 */
export default function ShippingPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="shipping-policy-page">
      <div className="eyebrow">Legal</div>
      <h1 className="h-display text-4xl md:text-6xl mt-2 leading-[1.04]">Shipping Policy.</h1>
      <p className="mt-4 text-[13px] font-mono uppercase tracking-[0.18em] text-[var(--ink-muted)]">
        Effective February 1, 2026
      </p>

      <div className="mt-6 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4 text-[var(--ink-soft)] text-sm leading-relaxed">
        The following core points govern every order shipped by Nebula Aminos LLC.
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card icon={<Clock className="h-5 w-5 text-[var(--terra)]" />} title="Order cutoff" testid="ship-cutoff">
          <p>Orders placed by <strong>3 PM EST</strong> ship the same or next business day.</p>
        </Card>

        <Card icon={<Truck className="h-5 w-5 text-[var(--terra)]" />} title="Carriers &amp; destinations" testid="ship-carriers">
          <p>We ship via USPS, UPS, or FedEx to U.S. addresses only.</p>
        </Card>

        <Card icon={<MapPin className="h-5 w-5 text-[var(--terra)]" />} title="Address requirements" testid="ship-address">
          <p>A valid, deliverable street address is required; we cannot ship to PO Boxes.</p>
        </Card>

        <Card icon={<Truck className="h-5 w-5 text-[var(--terra)]" />} title="Tracking" testid="ship-tracking">
          <p>Tracking is provided on dispatch. You will receive a shipment notification email
          with the carrier tracking link the moment your order leaves our facility.</p>
        </Card>
      </div>

      <div className="mt-10 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-5 flex items-start gap-3" data-testid="ship-carrier-liability">
        <AlertOctagon className="h-5 w-5 text-[var(--terra)] shrink-0 mt-0.5" />
        <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
          Nebula Aminos is not responsible for carrier delays once a package leaves our facility,
          but we will assist with claims for lost or damaged shipments. Please report any lost or
          damaged shipment within 7 days of the scheduled delivery date to{" "}
          <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a> with
          your order and tracking number.
        </p>
      </div>

      <div className="mt-12 text-[var(--ink-soft)]">
        <h2 className="font-display text-2xl font-medium text-[var(--ink)]">Contact</h2>
        <p className="mt-3">Nebula Aminos LLC · 113 W Ryker Ln, Midvale UT 84047, USA · 305-877-7739 ·{" "}
          <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a></p>
      </div>
    </div>
  );
}

function Card({ icon, title, children, testid }) {
  return (
    <div className="border border-[var(--line)] bg-[var(--paper)] rounded-md p-5" data-testid={testid}>
      <div className="flex items-start gap-2.5">
        {icon}
        <div className="font-display text-lg font-medium text-[var(--ink)]">{title}</div>
      </div>
      <div className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">{children}</div>
    </div>
  );
}
